import React, { useCallback, useEffect, useState } from 'react'
import useScanGun from '../../hooks/useScanGun'
import { commitMove, commitPutaway, defaultPositionFor, searchItems } from '../../utils/warehouseApi'
import { signal } from '../../utils/feedback'
import { parseScan } from '../../utils/scanCode'
import { resolveScan } from './resolveScan'
import ScanPrompt from './ScanPrompt'
import ItemRows from './ItemRows'
import PdaPhotos from './PdaPhotos'
import NeedsLabelButton from './NeedsLabelButton'

/**
 * Scan an item, scan a location, done.
 *
 * There is no confirmation step. Committing on the location scan is the whole
 * point of a trigger — an operator holding a guitar in one hand cannot also tap
 * "confirm", and the scan itself is the confirmation.
 *
 * Correcting a mistake is therefore just scanning the right location again:
 * the item stays in hand after a commit, and a second location scan moves it.
 * That is also why there is no undo button — Prism has no endpoint that
 * un-places an item, so an offer to undo a first putaway would be a lie.
 */

// Same defaults as the camera putaway page, so both lists show the same work.
const WORKLIST = { status: 'draft,in_stock', has_location: false, limit: 50 }

// A typed search deliberately drops `has_location`. The worklist is "what still
// needs a home", but someone searching by name is usually looking for an item
// precisely because they want to move it, and filtering those out would hide
// the thing they are holding. Same reasoning as the camera page.
const SEARCH = { status: 'draft,in_stock', limit: 50 }

// The camera flow refuses tote racks ("not supported in Phase 0b"). The
// handheld does not: totes and shelves are one flat namespace of scannable
// location codes, which is exactly what Consolidate moves things between.
const BLOCKED_STATUS = {
    full: 'That location is full. Scan another.',
    deleted: 'That location no longer exists. Scan another.'
}

export default function PdaPutaway() {
    const [held, setHeld] = useState(null)
    const [banner, setBanner] = useState(null)
    const [busy, setBusy] = useState(false)
    const [worklist, setWorklist] = useState([])
    // null means "not searching"; an array is the result set, empty included.
    const [results, setResults] = useState(null)
    const [query, setQuery] = useState('')

    const loadWorklist = useCallback(async () => {
        try {
            const response = await searchItems(WORKLIST)
            setWorklist(Array.isArray(response) ? response : response?.items ?? [])
        } catch {
            // The worklist is a convenience; scanning works without it, so a
            // failure here must not block the operator.
            setWorklist([])
        }
    }, [])

    useEffect(() => { loadWorklist() }, [loadWorklist])

    const place = useCallback(async (item, location) => {
        setBusy(true)
        try {
            const position = defaultPositionFor(location.shelf_type ?? location.type)
            const request = item.current_shelf_id
                ? commitMove({
                    itemId: item.item_id,
                    fromShelf: item.current_shelf_id,
                    fromPosition: item.current_position,
                    toShelf: location.shelf_id,
                    toPosition: position,
                    reason: 'reorganisation'
                })
                : commitPutaway({ itemId: item.item_id, shelfId: location.shelf_id, position })

            const placed = await request
            // Keep the item in hand with its new home, so scanning another
            // location corrects this one instead of starting over.
            setHeld({ ...item, current_shelf_id: placed.shelf_id, current_position: placed.position })
            // A location Prism had no row for is legitimate — every empty tote
            // is one — but it is worth seeing, so a mis-scan that happens to be
            // well formed does not quietly mint a tote nobody can find.
            setBanner(location.is_new
                ? { kind: 'warn', text: `New location ${placed.shelf_id} created · ${item.title}` }
                : { kind: 'success', text: `${item.title} → ${placed.shelf_id}` })
            signal('done')
            loadWorklist()
        } catch (error) {
            setBanner({ kind: 'error', text: error.message || 'Could not save that.' })
            signal('problem')
        } finally {
            setBusy(false)
        }
    }, [loadWorklist])

    const onScan = useCallback(async (raw) => {
        // A scan landing mid-commit is refused rather than queued: it is
        // ambiguous whether it belongs to the item being saved or the next
        // one. The tone tells the operator to pull the trigger again.
        if (busy) {
            signal('problem')
            return
        }
        setBanner(null)
        const result = await resolveScan(raw)

        if (result.kind === 'error') {
            setBanner({ kind: 'error', text: result.message })
            signal('problem')
            return
        }
        if (result.kind === 'item') {
            setHeld(result.item)
            signal('ok')
            return
        }
        if (!held) {
            setBanner({ kind: 'warn', text: 'Scan an item first, then the location.' })
            signal('problem')
            return
        }
        const blocked = BLOCKED_STATUS[result.location.status]
        if (blocked) {
            setBanner({ kind: 'error', text: blocked })
            signal('problem')
            return
        }
        place(held, result.location)
    }, [busy, held, place])

    /**
     * The typed box is an inventory search as well as a code reader.
     *
     * A real code goes straight through, so typing an inventory id or a shelf
     * id behaves exactly like scanning one — that is the fallback for a label
     * too damaged to read. Anything else is treated as a search term rather
     * than rejected, because "Fender Strat" is what someone types when they
     * cannot read the label at all.
     *
     * The gun does not get this behaviour: a trigger-pull that decodes to
     * something unrecognised is a misread, and quietly listing guesses would
     * be worse than saying so.
     */
    const onTyped = useCallback(async (text) => {
        if (parseScan(text).kind !== 'unknown') {
            onScan(text)
            return
        }
        setBanner(null)
        setQuery(text)
        setBusy(true)
        try {
            const response = await searchItems({ ...SEARCH, q: text })
            setResults(Array.isArray(response) ? response : response?.items ?? [])
        } catch (error) {
            setResults([])
            setBanner({ kind: 'error', text: error.message || 'Search failed.' })
        } finally {
            setBusy(false)
        }
    }, [onScan])

    const clearSearch = useCallback(() => {
        setResults(null)
        setQuery('')
        setBanner(null)
    }, [])

    // Always listening. Gating on `busy` tore the listener down mid-request and
    // cleared its buffer, so a scan arriving during one was swallowed whole.
    useScanGun(onScan)

    return (
        <>
            <ScanPrompt
                prompt={held ? 'Scan the location' : 'Scan an item'}
                hint={held ? 'A shelf or tote label.' : 'Or tap one from the list below.'}
                placeholder={held ? 'Locator code' : 'Inventory lookup'}
                onSubmit={onTyped}
                disabled={busy}
            />

            {banner && <div className={`pda-banner is-${banner.kind}`}>{banner.text}</div>}

            {held && (
                <div className="pda-held">
                    <div className="pda-held-title">{held.title}</div>
                    <div className="pda-code">{held.item_id}</div>
                    {held.current_shelf_id && (
                        <div className="pda-location">{held.current_shelf_id}</div>
                    )}
                    <PdaPhotos item={held} />
                </div>
            )}

            {held && (
                <>
                    <NeedsLabelButton item={held} disabled={busy} />
                    <button className="pda-btn" onClick={() => { setHeld(null); setBanner(null) }} disabled={busy}>
                        Next item
                    </button>
                </>
            )}

            {!held && results !== null && (
                <>
                    <div className="pda-sub">
                        {results.length} {results.length === 1 ? 'match' : 'matches'} for “{query}”
                    </div>
                    <ItemRows
                        items={results}
                        emptyText={`No inventory matching “${query}”.`}
                        onPick={setHeld}
                    />
                    <button className="pda-btn" onClick={clearSearch} disabled={busy}>
                        Clear search
                    </button>
                </>
            )}

            {!held && results === null && (
                <ItemRows
                    items={worklist}
                    emptyText="Nothing waiting to be put away."
                    onPick={setHeld}
                />
            )}
        </>
    )
}
